import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useNavigate } from 'react-router';
import { carsQuery } from './api/cars';
import { channelsQuery } from './api/channels';
import { ApiError, login } from './api/client';
import { AuthProvider } from './auth/AuthProvider';
import { PhoneRequest } from './auth/PhoneRequest';
import { MainButtonHost } from './hooks/useMainButton';
import { FleetPage } from './pages/FleetPage';
import { rawInitData, startParam } from './telegram';
import { ListSkeleton, TabBar, ToastProvider } from './ui';

/**
 * T7.3: the fleet screen ships in the first chunk; every other screen is its own chunk, fetched
 * on idle right after start so that opening it later does not wait for the network.
 */
const screens = {
  channels: () => import('./pages/ChannelsPage').then((m) => ({ Component: m.ChannelsPage })),
  template: () => import('./pages/TemplatePage').then((m) => ({ Component: m.TemplatePage })),
  newCar: () => import('./pages/new-car/NewCarPage').then((m) => ({ Component: m.NewCarPage })),
  car: () => import('./pages/car/CarPage').then((m) => ({ Component: m.CarPage })),
  editCar: () => import('./pages/car/EditCarPage').then((m) => ({ Component: m.EditCarPage })),
  photos: () => import('./pages/car/PhotosPage').then((m) => ({ Component: m.PhotosPage })),
};

export function prefetchScreens(): void {
  const run = () => {
    for (const load of Object.values(screens)) void load().catch(() => undefined);
  };
  // After the fleet and its photos had the network to themselves.
  const later = () =>
    setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run);
      else run();
    }, 2_000);
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
}

/** Routes a bot deep link (`startapp=car_<id>`, `channels`, `template`) once on launch. */
export function routeForStartParam(param: string | undefined): string | null {
  if (!param) return null;
  const car = /^car_([0-9a-f-]{36})$/i.exec(param);
  if (car) return `/cars/${car[1]}`;
  if (param === 'channels') return '/channels';
  if (param === 'template') return '/template';
  if (param === 'new_car') return '/cars/new';
  return null;
}

let deepLinkHandled = false;
function DeepLink() {
  const navigate = useNavigate();
  useEffect(() => {
    if (deepLinkHandled) return;
    deepLinkHandled = true;
    const target = routeForStartParam(startParam());
    if (target) navigate(target, { replace: true });
  }, [navigate]);
  return null;
}

function Root() {
  return (
    <AuthProvider>
      <DeepLink />
      <PhoneRequest />
      <Outlet />
    </AuthProvider>
  );
}

function TabLayout() {
  return (
    <>
      <Outlet />
      <TabBar />
    </>
  );
}

const router = createBrowserRouter([
  // Dev-only component catalogue, reachable without Telegram.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/kit',
          lazy: () => import('./pages/KitPage').then((m) => ({ Component: m.KitPage })),
        },
      ]
    : []),
  {
    element: <Root />,
    // Shown only when the app starts on a screen whose chunk is not loaded yet (a deep link).
    hydrateFallbackElement: <ListSkeleton rows={4} />,
    children: [
      {
        element: <TabLayout />,
        children: [
          { path: '/', element: <FleetPage /> },
          { path: '/channels', lazy: screens.channels },
          { path: '/template', lazy: screens.template },
        ],
      },
      { path: '/cars/new', lazy: screens.newCar },
      { path: '/cars/:id', lazy: screens.car },
      { path: '/cars/:id/edit', lazy: screens.editCar },
      { path: '/cars/:id/photos', lazy: screens.photos },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * T7.3: on a plain launch the login and the fleet requests start before React renders, so the
 * data arrives one render cycle earlier. The fleet requests wait for the login inside `request`.
 */
export function warmStart(): void {
  if (!rawInitData() || window.location.pathname !== '/' || startParam()) return;
  void queryClient.prefetchQuery({
    queryKey: ['session'],
    queryFn: login,
    staleTime: Number.POSITIVE_INFINITY,
  });
  void queryClient.prefetchQuery(carsQuery('all', ''));
  void queryClient.prefetchQuery(channelsQuery);
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <MainButtonHost>
          <RouterProvider router={router} />
        </MainButtonHost>
      </ToastProvider>
    </QueryClientProvider>
  );
}
